import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError, money, money5 } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  CurrencyDollar, ArrowClockwise, MagnifyingGlass, DownloadSimple, WaveSine, Robot,
} from "@phosphor-icons/react";
import DateRangePicker, { rangeToParams } from "@/components/DateRangePicker";
import { downloadCsv } from "@/lib/csv";

const last30 = () => {
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  const from = new Date(to);
  from.setDate(from.getDate() - 29);
  return { from, to };
};

const KIND_LABEL = {
  channel_purchase: "Channel purchase",
  channel_renewal: "Channel renewal",
  ai_agent: "AI agent",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
const pct = (x100) => `${((x100 || 0) / 100).toFixed(2)}%`;

export default function Commissions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [details, setDetails] = useState([]);
  const [summary, setSummary] = useState({
    channel_spend_minor: 0,
    ai_spend_minor: 0,
    channels_earned_x1e5: 0,
    ai_earned_x1e5: 0,
    total_earned_x1e5: 0,
  });
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(last30);
  const [userId, setUserId] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin-users")
      .then(({ data }) => setStaffList(data || []))
      .catch(() => {});
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...rangeToParams(range) };
      if (isAdmin && userId !== "all") params.user_id = userId;
      const { data } = await api.get("/reports/commissions", { params });
      setDetails(data?.details || []);
      setSummary(data?.summary || {
        channel_spend_minor: 0, ai_spend_minor: 0,
        channels_earned_x1e5: 0, ai_earned_x1e5: 0, total_earned_x1e5: 0,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [range, userId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return details;
    return details.filter((r) => {
      const hay = [
        r.user_name, r.account_name, r.account_email, r.label, KIND_LABEL[r.kind],
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [details, q]);

  const cards = useMemo(() => {
    if (!q.trim()) return summary;
    let channel_spend = 0, ai_spend = 0, ch_earned = 0, ai_earned = 0;
    filtered.forEach((r) => {
      if (r.kind === "ai_agent") {
        ai_spend += r.amount_minor || 0;
        ai_earned += r.earned_x1e5 || 0;
      } else {
        channel_spend += r.amount_minor || 0;
        ch_earned += r.earned_x1e5 || 0;
      }
    });
    return {
      channel_spend_minor: channel_spend,
      ai_spend_minor: ai_spend,
      channels_earned_x1e5: ch_earned,
      ai_earned_x1e5: ai_earned,
      total_earned_x1e5: ch_earned + ai_earned,
    };
  }, [filtered, summary, q]);

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Nothing to export");
      return;
    }
    const cols = [
      ...(isAdmin ? [{ label: "Staff user", value: (r) => r.user_name || "" }] : []),
      { label: "Date", value: (r) => fmtDate(r.created_at) },
      { label: "Account", value: (r) => r.account_name || "" },
      { label: "Account email", value: (r) => r.account_email || "" },
      { label: "Type", value: (r) => KIND_LABEL[r.kind] || r.kind },
      { label: "Description", value: (r) => r.label || "" },
      { label: "Purchase / charge ($)", value: (r) => ((r.amount_minor || 0) / 100).toFixed(2) },
      { label: "Rate (%)", value: (r) => ((r.rate_x100 || 0) / 100).toFixed(2) },
      { label: "Commission ($)", value: (r) => ((r.earned_x1e5 || 0) / 100000).toFixed(5) },
    ];
    downloadCsv("commissions.csv", cols, filtered);
  };

  return (
    <div>
      <PageHeader
        title="Commissions"
        subtitle={
          isAdmin
            ? "Commission = purchase/charge amount × staff %. Example: $55.65 channel buy × 20% = $11.13."
            : "Your commission = purchase/charge amount on your assigned accounts × your %. Example: $55.65 × 20% = $11.13."
        }
      >
        <Button variant="outline" onClick={load} disabled={loading} data-testid="commissions-refresh">
          <ArrowClockwise className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
        <Button variant="outline" onClick={exportCsv} data-testid="commissions-export">
          <DownloadSimple className="w-4 h-4 mr-1.5" /> Export
        </Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-5">
        <div className="w-full sm:w-[280px]">
          <DateRangePicker value={range} onChange={setRange} testid="commissions-range" />
        </div>
        {isAdmin && (
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="w-full sm:w-[220px]" data-testid="commissions-user-filter">
              <SelectValue placeholder="All users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {staffList.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder={isAdmin ? "Search user or account…" : "Search account…"}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="commissions-search"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard icon={WaveSine} label="Channel purchases" value={money(cards.channel_spend_minor)} />
        <StatCard icon={CurrencyDollar} label="Channel commission" value={money5(cards.channels_earned_x1e5)} />
        <StatCard icon={Robot} label="AI charges" value={money(cards.ai_spend_minor)} />
        <StatCard icon={CurrencyDollar} label="Total commission" value={money5(cards.total_earned_x1e5)} />
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={CurrencyDollar}
          title="No commission yet"
          description={
            isAdmin
              ? "When an assigned customer/reseller buys channels or uses AI, purchase amount × staff % appears here."
              : "When your assigned customers/resellers buy channels or use AI, purchase amount × your % appears here."
          }
        />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="commissions-table">
          <Table>
            <TableHeader>
              <TableRow>
                {isAdmin && <TableHead>Staff</TableHead>}
                <TableHead>Date</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Purchase amount</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Commission</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={`${r.kind}-${r.id}`} data-testid={`commission-row-${r.id}`}>
                  {isAdmin && (
                    <TableCell className="font-medium whitespace-nowrap">{r.user_name}</TableCell>
                  )}
                  <TableCell className="text-muted-foreground whitespace-nowrap text-sm">
                    {fmtDate(r.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{r.account_name}</div>
                    <div className="text-xs text-muted-foreground">{r.account_email || "—"}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{KIND_LABEL[r.kind] || r.kind}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[220px]">{r.label}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{money(r.amount_minor)}</TableCell>
                  <TableCell className="text-right tabular-nums">{pct(r.rate_x100)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{money5(r.earned_x1e5)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
